import { useCallback, useRef, useEffect } from 'react';

import { SignMailRecordingStatusWidget } from 'features/signmail/components';
import {
  useSignMailRecording,
  useSignMailRecordingTimer,
} from 'features/signmail/hooks';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

export const SignMailRecording = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { start } = useSignMailRecording();
  const { startRecordingTimer } = useSignMailRecordingTimer();
  const hasStartedRef = useRef(false);
  const isMountedRef = useRef(true);

  const startRecording = useCallback(async () => {
    if (hasStartedRef.current || !isMountedRef.current) {
      return;
    }
    if (!videoRef.current) {
      console.warn('videoRef.current is null; recording not started');
      return;
    }
    hasStartedRef.current = true;

    try {
      await start(videoRef);
      startRecordingTimer();
    } catch (error) {
      console.error('Failed to start SignMail recording:', error);
      hasStartedRef.current = false;
    }
  }, [start, startRecordingTimer]);

  const handleVideoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node;
      if (node) {
        startRecording();
      }
    },
    [startRecording]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Container data-testid='signmail-recording'>
      <SignMailRecordingStatusWidget />
      <StyledVideo ref={handleVideoRef} autoPlay />
    </Container>
  );
};

const Container = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: ${theme.palette.grey[900]};
`;

const StyledVideo = styled('video')`
  width: 100%;
  height: 100%;
  transform: rotateY(180deg);
`;
